// src/components/WarehouseDetail.js
// Entrepôt — /warehouses-instructions/:warehouseId. Header (breadcrumb, name,
// one-line scope, « ⋯ » › Supprimer, « Modifier ») · Contact | Clients B2B ·
// the AI's instructions, readable as a document. A missing address or
// missing instructions is said in its own card, with the action that fixes
// it. Edit happens in place with the shared form.
// The API has no single-GET endpoint: the enriched list
// (GET /api/logistics/warehouses-edn) is the source, picked by id.
// Writes: PUT / DELETE /api/logistics/warehouses-edn/{id}.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Building2, ChevronRight, Mail, MailX, Pencil, Plus, ScrollText, Trash2, Warehouse,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import WarehouseForm from './WarehouseForm';
import {
  getWarehouseEmails, hasInstructions, sortByName, validateWarehouseForm, warehouseFormFrom, warehousePayload,
} from './warehousesModel';
import { formatDay } from './clientsShared';
import './WarehouseDetail.css';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const WarehouseDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { warehouseId } = useParams();

  const [warehouse, setWarehouse] = useState(undefined); // undefined = loading, null = not found
  const [clients, setClients] = useState([]);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState(null); // non-null = editing
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchWarehouse = useCallback(async () => {
    try {
      const res = await axios.get('/api/logistics/warehouses-edn', { headers: authHeaders() });
      const found = (res.data || []).find((w) => String(w.id) === String(warehouseId));
      setWarehouse(found || null);
    } catch (error) {
      console.error('Failed to fetch warehouse:', error);
      setWarehouse(null);
    }
  }, [warehouseId]);

  useEffect(() => {
    fetchWarehouse();
    axios.get('/api/logistics/clients-edn', { headers: authHeaders() })
      .then((res) => setClients(Array.isArray(res.data) ? res.data : []))
      .catch((error) => console.error('Failed to fetch clients:', error));
  }, [fetchWarehouse]);

  const startEditing = () => {
    setForm(warehouseFormFrom(warehouse));
    setFormError(null);
  };

  const cancelEditing = () => {
    setForm(null);
    setFormError(null);
  };

  const handleSave = async () => {
    const error = validateWarehouseForm(form, t);
    if (error) { setFormError(error); return; }
    setSaving(true);
    setFormError(null);
    try {
      await axios.put(`/api/logistics/warehouses-edn/${warehouseId}`, warehousePayload(form), { headers: authHeaders() });
      setForm(null);
      setToast({ tone: 'success', text: t('warehousesInstructions.updateSuccess', 'Entrepôt mis à jour.') });
      fetchWarehouse();
    } catch (error) {
      console.error('Failed to update warehouse:', error);
      const detail = error.response?.data?.detail;
      setFormError(typeof detail === 'string' ? detail : t('warehousesInstructions.updateError', 'Échec de la mise à jour de l’entrepôt.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/logistics/warehouses-edn/${warehouseId}`, { headers: authHeaders() });
      navigate('/warehouses-instructions');
    } catch (error) {
      console.error('Failed to delete warehouse:', error);
      setDeleting(false);
      setConfirmDelete(false);
      setToast({ tone: 'danger', text: t('warehousesInstructions.deleteError', 'Échec de la suppression de l’entrepôt.') });
    }
  };

  const breadcrumb = (
    <>
      <Link to="/warehouses-instructions">{t('warehousesInstructions.title', 'Entrepôts')}</Link>
      <ChevronRight aria-hidden="true" className="whd-v2__crumb-sep" />
    </>
  );

  if (warehouse === undefined) {
    return (
      <div className="u-page whd-v2">
        <div className="whd-v2__skeleton" aria-busy="true" aria-label={t('aiAutomationUi.warehouses.loadingOne', 'Chargement de l’entrepôt')}>
          <span className="u-skeleton whd-v2__skeleton-title" />
          {Array.from({ length: 4 }, (_, i) => <span key={i} className="u-skeleton" />)}
        </div>
      </div>
    );
  }

  if (warehouse === null) {
    return (
      <div className="u-page whd-v2">
        <PageHeader breadcrumb={breadcrumb} title={t('warehousesInstructions.notFound', 'Entrepôt introuvable.')} />
        <div className="u-well">
          <section className="u-card">
            <EmptyState
              icon={Warehouse}
              title={t('warehousesInstructions.notFound', 'Entrepôt introuvable.')}
              action={(
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => navigate('/warehouses-instructions')}>
                  <ArrowLeft aria-hidden="true" />
                  {t('warehousesInstructions.backToList', 'Retour aux entrepôts')}
                </button>
              )}
            >
              {t('aiAutomationUi.warehouses.notFoundHint', 'Il a peut-être été supprimé. La liste des entrepôts est à jour.')}
            </EmptyState>
          </section>
        </div>
      </div>
    );
  }

  const emails = getWarehouseEmails(warehouse);
  const clientList = sortByName(warehouse.clients || []);
  const editing = form !== null;
  const subtitleParts = [
    t('aiAutomationUi.warehouses.detail.emails', '{{count}} adresses', { count: emails.length }),
    t('aiAutomationUi.warehouses.detail.clients', '{{count}} clients B2B', { count: clientList.length }),
  ];
  if (warehouse.updated_at) subtitleParts.push(t('aiAutomationUi.warehouses.detail.updated', 'modifié le {{date}}', { date: formatDay(warehouse.updated_at) }));


  return (
    <div className="u-page whd-v2">
      <PageHeader
        breadcrumb={breadcrumb}
        title={warehouse.name}
        subtitle={subtitleParts.join(' · ')}
        actions={editing ? null : (
          <>
            <OverflowMenu
              label={t('aiAutomationUi.warehouses.detail.actions', 'Actions de l’entrepôt')}
              items={[
                { label: t('common.delete', 'Supprimer'), icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) },
              ]}
            />
            <button type="button" className="u-btn u-btn--primary" onClick={startEditing}>
              <Pencil aria-hidden="true" />
              {t('warehousesInstructions.edit', 'Modifier')}
            </button>
          </>
        )}
      />

      <div className="u-well">
        {editing ? (
          <Card
            icon={Pencil}
            title={t('aiAutomationUi.warehouses.detail.editTitle', 'Modifier l’entrepôt')}
            footerNote={t('aiAutomationUi.warehouses.detail.editNote', 'Les changements valent pour les prochains échanges de l’IA avec cet entrepôt.')}
            footer={(
              <>
                <button type="button" className="u-btn u-btn--secondary" onClick={cancelEditing} disabled={saving}>
                  {t('common.cancel', 'Annuler')}
                </button>
                <button type="button" className="u-btn u-btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? t('warehousesInstructions.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
                </button>
              </>
            )}
          >
            {formError && <div className="whd-v2__error"><Banner tone="danger">{formError}</Banner></div>}
            <WarehouseForm
              form={form}
              setForm={(updater) => { setForm(updater); setFormError(null); }}
              clients={clients}
              t={t}
              idPrefix="whd"
            />
          </Card>
        ) : (
          <>
            <div className="u-grid-2">
              <Card
                icon={Mail}
                title={t('aiAutomationUi.warehouses.detail.contactTitle', 'Contact')}
                badge={emails.length ? <Badge>{emails.length}</Badge> : null}
                description={t('warehousesInstructions.sectionContactHint', 'Adresses utilisées par l’IA pour contacter l’entrepôt.')}
                flush
              >
                {emails.length === 0 ? (
                  <EmptyState
                    icon={MailX}
                    title={t('aiAutomationUi.warehouses.detail.noEmail', 'Aucune adresse e-mail')}
                    action={(
                      <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={startEditing}>
                        <Plus aria-hidden="true" />
                        {t('aiAutomationUi.warehouses.form.addEmail', 'Ajouter une adresse')}
                      </button>
                    )}
                  >
                    <span className="whd-v2__blocking">{t('warehousesInstructions.noEmailHint', 'Aucun email — l’IA ne peut pas contacter cet entrepôt.')}</span>
                  </EmptyState>
                ) : (
                  <ul className="u-list">
                    {emails.map((email, index) => (
                      <li key={email} className="u-row">
                        <div className="u-row__main">
                          <div className="u-row__title">
                            <a className="u-link whd-v2__email" href={`mailto:${email}`}>{email}</a>
                            {index === 0 && emails.length > 1 && <Badge>{t('aiAutomationUi.warehouses.detail.primary', 'Principale')}</Badge>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card
                icon={Building2}
                title={t('warehousesInstructions.sectionClients', 'Clients associés')}
                badge={clientList.length ? <Badge>{clientList.length}</Badge> : null}
                description={t('aiAutomationUi.warehouses.form.clientsHelp', 'Les clients B2B dont les colis partent de cet entrepôt.')}
                flush
              >
                {clientList.length === 0 ? (
                  <EmptyState icon={Building2} title={t('warehousesInstructions.noClientsAssociated', 'Aucun client associé à cet entrepôt.')}>
                    {t('aiAutomationUi.warehouses.detail.noClientsHint', 'Associez des clients B2B depuis « Modifier ».')}
                  </EmptyState>
                ) : (
                  <ul className="u-list whd-v2__clients">
                    {clientList.map((client) => (
                      <li key={client.id} className="u-row">
                        <div className="u-row__main">
                          <div className="u-row__title">{client.name}</div>
                        </div>
                        <div className="u-row__side">
                          <Link className="u-btn u-btn--ghost u-btn--sm" to={`/clients-instructions/${client.id}`}>
                            {t('aiAutomationUi.warehouses.open', 'Ouvrir')}
                            <ChevronRight aria-hidden="true" />
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            <Card
              icon={ScrollText}
              title={t('warehousesInstructions.sectionInstructions', 'Instructions IA')}
              description={t('warehousesInstructions.sectionInstructionsHint', 'Consignes suivies par l’IA lors des échanges avec cet entrepôt.')}
            >
              {hasInstructions(warehouse) ? (
                <div className="whd-v2__doc">{warehouse.instructions}</div>
              ) : (
                <EmptyState
                  icon={ScrollText}
                  title={t('aiAutomationUi.warehouses.detail.noInstr', 'Aucune consigne pour l’IA')}
                  action={(
                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={startEditing}>
                      <Pencil aria-hidden="true" />
                      {t('aiAutomationUi.warehouses.detail.write', 'Écrire les consignes')}
                    </button>
                  )}
                >
                  {t('warehousesInstructions.instructionsMissingHint', 'À compléter — l’IA contactera cet entrepôt sans consigne particulière.')}
                </EmptyState>
              )}
            </Card>
          </>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          icon={Trash2}
          title={t('aiAutomationUi.warehouses.detail.deleteTitle', 'Supprimer l’entrepôt {{name}} ?', { name: warehouse.name })}
          message={t('aiAutomationUi.warehouses.detail.deleteMessage', 'Ses adresses et ses consignes sont effacées : l’IA ne le contactera plus. Les {{count}} clients B2B associés ne sont pas supprimés.', { count: clientList.length })}
          confirmLabel={deleting ? t('aiAutomationUi.warehouses.detail.deleting', 'Suppression…') : t('common.delete', 'Supprimer')}
          cancelLabel={t('common.cancel', 'Annuler')}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          busy={deleting}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default WarehouseDetail;
